import React from "react";
import * as Mui from "@mui/material";
import * as Icons from "react-feather";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { FeatherIcon } from "component/shared/feather-icon";
import { SearchAgentProfile } from "model/search-agent-profile";
import { SparkleIcon, BedIcon, BathIcon } from "../icons";
import { formatProfilePrice, formatProfileTimeline, formatProfilePreApproval } from "./format";
import { styles } from "./style";

interface Props extends WithStyles<typeof styles> {
	profile?: SearchAgentProfile;
	/** Shows the "Clear search profile history" action when provided and the profile has content. */
	onClearHistory?: () => void;
	/** Replaces the card body with a load-failure message, so a failed fetch never reads as empty. */
	error?: boolean;
}

const hasText = (value?: string | null) => !!value && !!value.trim();

const nonBlank = (values?: string[] | null) => (values || []).filter(hasText);

/**
 * A profile is empty when it has neither insights nor any preference field. The API returns
 * empty lists and absent scalars for a lead with no generated profile, and that is also what
 * clearing the profile history leaves behind, so both read as empty here. Blank strings count
 * as absent; a bedroom or bathroom count of zero counts as a value. The price, timeline and
 * pre-approval fields count when their row would render.
 */
export const isSearchProfileEmpty = (profile?: SearchAgentProfile | null) => {
	if (!profile) {
		return true;
	}
	return !hasText(profile.insights)
		&& profile.bedrooms == null
		&& profile.bathrooms == null
		&& !nonBlank(profile.locations).length
		&& !nonBlank(profile.mustHaves).length
		&& !nonBlank(profile.niceToHaves).length
		&& !nonBlank(profile.dealBreakers).length
		&& !nonBlank(profile.propertyTypes).length
		&& formatProfilePrice(profile.priceMin, profile.priceMax) === undefined
		&& formatProfileTimeline(profile.timeline) === undefined
		&& formatProfilePreApproval(profile.preApproved) === undefined;
};

interface AttributeRow {
	key: string;
	label: React.ReactNode;
	value?: string;
}

class Component extends React.Component<Props> {

	private get attributeRows(): AttributeRow[] {
		const { profile, classes = {} } = this.props;
		if (!profile) {
			return [];
		}
		const { bedrooms, bathrooms } = profile;
		// Beds and baths share one row in the design, rendered as "3 / 2". Compared against
		// null rather than truthiness so a genuine zero is not mistaken for an unset field.
		const hasBedsBaths = bedrooms != null || bathrooms != null;
		const bedsBaths = hasBedsBaths
			? [bedrooms ?? "—", bathrooms ?? "—"].join(" / ")
			: undefined;
		return [
			{
				key: "locations",
				label: (
					<>
						<FeatherIcon className={classes.labelIcon}><Icons.MapPin /></FeatherIcon>
						<span>Locations</span>
					</>
				),
				value: this.join(profile.locations),
			},
			{
				key: "propertyTypes",
				label: (
					<>
						<FeatherIcon className={classes.labelIcon}><Icons.Home /></FeatherIcon>
						<span>Property types</span>
					</>
				),
				value: this.join(profile.propertyTypes),
			},
			{
				key: "bedsBaths",
				label: (
					<>
						<BedIcon className={classes.labelIcon} />
						<span>Bedrooms</span>
						<span className={classes.labelSeparator}>/</span>
						<BathIcon className={classes.labelIcon} />
						<span>Bathrooms</span>
					</>
				),
				value: bedsBaths,
			},
			{
				key: "price",
				label: (
					<>
						<FeatherIcon className={classes.labelIcon}><Icons.DollarSign /></FeatherIcon>
						<span>Price</span>
					</>
				),
				value: formatProfilePrice(profile.priceMin, profile.priceMax),
			},
			{
				key: "timeline",
				label: (
					<>
						<FeatherIcon className={classes.labelIcon}><Icons.Calendar /></FeatherIcon>
						<span>Timeline</span>
					</>
				),
				value: formatProfileTimeline(profile.timeline),
			},
			{
				key: "preApproval",
				label: (
					<>
						<FeatherIcon className={classes.labelIcon}><Icons.FileText /></FeatherIcon>
						<span>Pre-approval</span>
					</>
				),
				value: formatProfilePreApproval(profile.preApproved),
			},
			{
				key: "mustHaves",
				label: (
					<>
						<FeatherIcon className={classes.labelIcon}><Icons.CheckCircle /></FeatherIcon>
						<span>Must-haves</span>
					</>
				),
				value: this.join(profile.mustHaves),
			},
			{
				key: "niceToHaves",
				label: (
					<>
						<FeatherIcon className={classes.labelIcon}><Icons.PlusCircle /></FeatherIcon>
						<span>Nice-to-haves</span>
					</>
				),
				value: this.join(profile.niceToHaves),
			},
			{
				key: "dealBreakers",
				label: (
					<>
						<FeatherIcon className={classes.labelIcon}><Icons.XCircle /></FeatherIcon>
						<span>Deal-breakers</span>
					</>
				),
				value: this.join(profile.dealBreakers),
			},
		];
	}

	private join(values?: string[]) {
		const items = nonBlank(values);
		return items.length ? items.join(", ") : undefined;
	}

	public render() {
		const { profile, onClearHistory, error, classes = {} } = this.props;
		const isEmpty = isSearchProfileEmpty(profile);
		// Only populated attributes are rendered, so a partially-built profile does not
		// show a column of blank values.
		const rows = this.attributeRows.filter((row) => !!row.value);
		// Clearing an empty profile would do nothing, so the action is only offered when
		// there is something to clear. Once a clear succeeds the card goes empty and the
		// action disappears with the content it removed.
		const showClearHistory = !!onClearHistory && !error && !isEmpty;

		return (
			<div className={classes.card}>
				<div className={classes.header}>
					<Mui.Typography className={classes.title}>Search Profile</Mui.Typography>
					{showClearHistory && (
						<Mui.Button
							className={classes.clearButton}
							size="small"
							color="secondary"
							onClick={onClearHistory}
						>
							Clear search profile history
						</Mui.Button>
					)}
				</div>
				{error ? (
					<Mui.Typography className={classes.message}>
						The search profile couldn't be loaded. Refresh the page to try again.
					</Mui.Typography>
				) : isEmpty ? (
					<Mui.Typography className={classes.message}>
						No search profile yet. Preferences are built as the lead chats with the Search Agent.
					</Mui.Typography>
				) : (
					<>
						{profile && hasText(profile.insights) && (
							<div className={classes.section}>
								<div className={classes.label}>
									<SparkleIcon className={classes.labelIcon} />
									<span>Insights</span>
								</div>
								<Mui.Typography className={classes.value}>{profile.insights}</Mui.Typography>
								<div className={classes.disclaimer}>
									<SparkleIcon className={classes.disclaimerIcon} />
									<span>AI-generated from conversation history. Review for accuracy before acting.</span>
								</div>
							</div>
						)}
						{rows.map((row) => (
							<div key={row.key} className={classes.row}>
								<div className={classes.label}>{row.label}</div>
								<Mui.Typography className={classes.value}>{row.value}</Mui.Typography>
							</div>
						))}
					</>
				)}
			</div>
		);
	}
}

export const SearchProfileCard = withStyles(Component, styles);
